import type { Result } from 'true-myth';

import type { CompanyProfile, CompanyProfileUpdate } from '~/shared/data-contract/company-profile';
import { companyProfileSchema } from '~/shared/data-contract/company-profile';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

type JsonFetcher = ValidatingHttpFetcherWithBaseUrl;

export type CompanyProfileWithStatus = {
    readonly data: CompanyProfile;
    readonly status: number;
};

export type CompanyProfileFetchers = {
    readonly fetchCompanyProfile: () => Promise<Result<CompanyProfile, HttpFetcherError>>;
    readonly fetchCompanyProfileWithStatus: () => Promise<Result<CompanyProfileWithStatus, HttpFetcherError>>;
    readonly updateCompanyProfile: (args: {
        readonly payload: CompanyProfileUpdate;
    }) => Promise<Result<CompanyProfile, HttpFetcherError>>;
};

export type CompanyProfileFetchersDependencies = {
    readonly validatingCobrainerApiFetcher: JsonFetcher;
};

export const createCompanyProfileFetchers = (
    dependencies: CompanyProfileFetchersDependencies,
): CompanyProfileFetchers => {
    const { validatingCobrainerApiFetcher } = dependencies;

    const fetchCompanyProfile: CompanyProfileFetchers['fetchCompanyProfile'] = async () => {
        return validatingCobrainerApiFetcher.get(companyProfileSchema, {
            pathname: '/company-profile',
        });
    };

    const fetchCompanyProfileWithStatus: CompanyProfileFetchers['fetchCompanyProfileWithStatus'] = () => {
        return validatingCobrainerApiFetcher.getWithStatus(companyProfileSchema, {
            pathname: '/company-profile',
        });
    };

    const updateCompanyProfile: CompanyProfileFetchers['updateCompanyProfile'] = async ({ payload }) => {
        return validatingCobrainerApiFetcher.patchJson(companyProfileSchema, {
            pathname: '/company-profile',
            payload,
        });
    };

    return {
        fetchCompanyProfile,
        fetchCompanyProfileWithStatus,
        updateCompanyProfile,
    };
};
